import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
    GetAllUsers,
    GetUserById,
    AddNewUser,
    UpdateUser,
    DeleteUser,
    UpdateUserMeetingRoomQuota,
    UpdateUserStatus,
    SendResetPasswordEmail as SendResetPasswordEmailBE,
    AddTeamMemberByAdmin,
} from '../services/backend';
import { Notify, NotifyError } from '../utils/functions';
import { SendResetPasswordEmail } from '../services/firebase/auth';
import { STATIC_USER_ROLES } from '../utils/enums';

export function useGetUsers(filters = {}) {
    return useQuery(['users', filters], () => GetAllUsers(filters), {
        refetchOnWindowFocus: false,
        staleTime: 5 * 60 * 1000, // 5 minutes
        select: (data) => {
            return data?.data || [];
        },
        onError: (error) => {
            NotifyError("Failed to fetch users", error);
            console.error("Error fetching users:", error);
        }
    });
}

export function useGetUserById(userId) {
    return useQuery(
        ['user', userId],
        () => GetUserById(userId),
        {
            enabled: !!userId,
            staleTime: 5 * 60 * 1000, // 5 minutes
            onError: (error) => {
                NotifyError("Failed to fetch user details", error);
                console.error("Error fetching user details:", error);
            }
        }
    );
}

export function useAddUser() {
    const queryClient = useQueryClient();
    return useMutation(AddNewUser, {
        onSuccess: () => {
            Notify("User added successfully", "success");
            queryClient.invalidateQueries('users');
        },
        onError: (error) => {
            NotifyError("Failed to add user", error);
            console.error("Error adding user:", error);
        }
    });
}

export function useUpdateUser() {
    const queryClient = useQueryClient();
    return useMutation(({ userId, data }) => UpdateUser(userId, data), {
        onSuccess: () => {
            Notify("User updated successfully", "success");
            queryClient.invalidateQueries('users');
        },
        onError: (error) => {
            NotifyError("Failed to update user", error);
            console.error("Error updating user:", error);
        }
    });
}

export function useDeleteUser() {
    const queryClient = useQueryClient();
    return useMutation(DeleteUser, {
        onSuccess: () => {
            Notify("User deleted successfully", "success");
            queryClient.invalidateQueries('users');
        },
        onError: (error) => {
            NotifyError("Failed to delete user", error);
            console.error("Error deleting user:", error);
        }
    });
}

export function useUpdateUserMeetingRoomQuota() {
    const queryClient = useQueryClient();
    return useMutation(({ userId, data }) => UpdateUserMeetingRoomQuota(userId, data), {
        onSuccess: () => {
            Notify("User meeting room quota updated successfully", "success");
            queryClient.invalidateQueries('users');
        },
        onError: (error) => {
            NotifyError("Failed to update user meeting room quota", error);
            console.error("Error updating user meeting room quota:", error);
        }
    });
}

export function useUpdateUserStatus() {
    const queryClient = useQueryClient();
    return useMutation(({ userId, status }) => UpdateUserStatus(userId, status), {
        onSuccess: () => {
            Notify("User status updated successfully", "success");
            queryClient.invalidateQueries('users');
        },
        onError: (error) => {
            NotifyError("Failed to update user status", error);
            console.error("Error updating user status:", error);
        }
    });
}

export function useSendResetPasswordEmail() {
    const queryClient = useQueryClient();
    return useMutation(async ({ email, userId }) => {
        await SendResetPasswordEmail(email)
        await SendResetPasswordEmailBE(userId);
    }, {
        onSuccess: () => {
            Notify("Reset password email sent successfully", "success");
            queryClient.invalidateQueries('users');
        },
        onError: (error) => {
            NotifyError("Failed to send reset password email", error);
            console.error("Error sending reset password email:", error);
        }
    });
}

export function useGetTeamMembers(customerId) {
    return useQuery(['team-members', customerId], () => GetAllUsers({
        role: STATIC_USER_ROLES.TEAM_MEMBER,
        teamId: customerId
    }), {
        enabled: !!customerId,
        staleTime: 5 * 60 * 1000, // 5 minutes
        select: (data) => {
            return data?.data || [];
        },
        onError: (error) => {
            NotifyError("Failed to fetch team members", error);
            console.error("Error fetching team members:", error);
        }
    });
}

export function useAddTeamMember() {
    const queryClient = useQueryClient();
    return useMutation(AddTeamMemberByAdmin, {
        onSuccess: () => {
            Notify("User team member updated successfully", "success");
            queryClient.invalidateQueries('users');
        },
        onError: (error) => {
            NotifyError("Failed to add team member", error);
            console.error("Error adding team member:", error);
        }
    });
}
